// Documento que vive em `alvaras_condominio`, mostrado fora da aba Alvarás.
//
// As abas Segurança e Licenças perguntavam "Possui AVCB?" e "Possui Habite-se?"
// como sim/não. Os dois são templates de alvará e já têm registro próprio, com
// número, emissão, vencimento e anexo — guardar um segundo sim/não faria a aba
// Alvarás dizer "vencido em 2024" enquanto a Segurança dizia "Sim, possui".
//
// Então elas não perguntam: mostram o registro e mandam para onde ele se edita.

import { Link } from 'react-router-dom';
import type { AlvaraRegistro, SituacaoAlvara } from '@/lib/condominioRepo';
import {
  CLASSE_SITUACAO,
  ROTULO_SITUACAO,
} from '@/pages/condominios/detail/components/situacaoAlvara';

export function SeloDeSituacao({ situacao }: { situacao: SituacaoAlvara }) {
  return (
    <span
      className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${CLASSE_SITUACAO[situacao]}`}
    >
      {ROTULO_SITUACAO[situacao]}
    </span>
  );
}

const formatData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

export default function DocumentoDoAlvara({
  titulo,
  explicacao,
  registro,
  condoId,
}: {
  titulo: string;
  explicacao: string;
  registro: AlvaraRegistro | null;
  condoId: string;
}) {
  const situacao: SituacaoAlvara = registro?.situacao ?? 'pendente';

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100/40 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-foreground-900">{titulo}</h4>
            <SeloDeSituacao situacao={situacao} />
          </div>
          <p className="text-xs text-foreground-500 mt-0.5">{explicacao}</p>
        </div>
        <Link
          to={`/condominios/${condoId}/alvaras`}
          className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors"
        >
          <i className="ri-external-link-line"></i>
          {registro ? 'Ver na aba Alvarás' : 'Registrar em Alvarás'}
        </Link>
      </div>

      {registro && registro.status !== 'pendente' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-background-200/60">
          <div>
            <p className="text-[11px] text-foreground-500">Documento</p>
            <p className="text-sm text-foreground-800 truncate">
              {registro.numeroDocumento || '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-500">Emissão</p>
            <p className="text-sm text-foreground-800">{formatData(registro.dataEmissao)}</p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-500">Vencimento</p>
            <p className="text-sm text-foreground-800">{formatData(registro.dataVencimento)}</p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-500">Anexos</p>
            <p className="text-sm text-foreground-800">{registro.anexos.length}</p>
          </div>
        </div>
      )}

      {registro?.status === 'nao_se_aplica' && registro.motivoNaoAplica && (
        <p className="text-xs text-foreground-500 mt-3 pt-3 border-t border-background-200/60">
          <span className="font-medium">Dispensado:</span> {registro.motivoNaoAplica}
        </p>
      )}
    </div>
  );
}
